import React, { Component } from 'react';
import { Box, Flex, Text, Icon, Card, Tooltip } from 'core/components';
import { FiCheckCircle, FiXCircle, FiArrowDown } from 'react-icons/fi';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import CloudIcon from 'app/views/hybrid/maps/components/CloudIcon';
import { convertToReadableId, convertToReadableType } from 'app/views/cloudConnectivity/utils';
import ToolResultCard from '../components/ToolResultCard';

const ICON_SIZE = 16;

function getSegmentCidr(segment) {
  return segment.entity?.CidrBlock ?? segment.entity?.PrivateIpAddress ?? null;
}

function renderMatchingRoute(matchingRoute) {
  if (!matchingRoute) {
    return null;
  }
  const dest = matchingRoute.DestinationCidrBlock ?? matchingRoute.matchingDestination?.address;
  return (
    <Box mt={1} pt={1} style={{ borderTop: '1px solid rgba(128,128,128,0.2)' }}>
      <Text as="div" small muted>
        Route: {dest} via {matchingRoute.GatewayId}
      </Text>
    </Box>
  );
}

function renderSegment(segment) {
  const readableId = convertToReadableId(segment.value, 'aws');
  const readableType = convertToReadableType(segment.type);
  const hasErrors = segment.errors?.length > 0;
  const cidr = getSegmentCidr(segment);

  return (
    <Card p={2} style={{ width: 300, minWidth: 300 }}>
      <Flex alignItems="flex-start" gap={1}>
        <Box mt="2px">
          <CloudIcon entity={segment.type} cloudProvider="aws" width={ICON_SIZE} height={ICON_SIZE} />
        </Box>
        <Box flex={1} style={{ overflow: 'hidden' }}>
          <Text as="div" fontWeight="bold" small>
            {readableType}
          </Text>
          <Text as="div" small muted style={{ wordBreak: 'break-all' }}>
            {readableId}
          </Text>
          {cidr && (
            <Text as="div" small muted>
              {cidr}
            </Text>
          )}
        </Box>
        {hasErrors && (
          <Tooltip
            intent="danger"
            content={
              <Box>
                {segment.errors.map((error) => (
                  <Text key={error.message} as="div">
                    {error.message}
                  </Text>
                ))}
              </Box>
            }
          >
            <Icon icon={BsFillExclamationTriangleFill} color="danger" iconSize={ICON_SIZE} />
          </Tooltip>
        )}
      </Flex>
      {renderMatchingRoute(segment.matchingRoute)}
    </Card>
  );
}

const GLOBAL_REGION = 'Global';

function groupByRegion(path) {
  const regionMap = [];
  let segmentsInRegion = [];

  path.forEach((segment, index) => {
    const region = segment.region ?? GLOBAL_REGION;
    const prevRegion = path[index - 1]?.region ?? GLOBAL_REGION;

    if (region !== prevRegion && index > 0) {
      regionMap.push({ region: prevRegion, regionPath: segmentsInRegion });
      segmentsInRegion = [];
    }

    segmentsInRegion.push(segment);

    if (index === path.length - 1) {
      regionMap.push({ region, regionPath: segmentsInRegion });
    }
  });

  return regionMap;
}

function renderPath(path, reachable) {
  const regionMap = groupByRegion(path);

  return (
    <Flex flexDirection="column" alignItems="center" gap={1}>
      <Flex alignItems="center" gap={1} mb={1}>
        <Icon icon={reachable ? FiCheckCircle : FiXCircle} color={reachable ? 'success' : 'danger'} iconSize={14} />
        <Text small fontWeight="bold" color={reachable ? 'success' : 'danger'}>
          {reachable ? 'Reachable' : 'Not Reachable'}
        </Text>
      </Flex>
      {regionMap.map(({ region, regionPath }, regionIdx) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${region}-${regionIdx}`}>
          <Box bg="subnavBackground" p={2} style={{ width: 316 }}>
            <Text small as="div" fontWeight="bold" mb={1}>
              Region: {region}
            </Text>
            <Flex flexDirection="column" alignItems="center" gap={1}>
              {regionPath.map((segment, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={`${segment.type}-${segment.value}-${idx}`}>
                  {renderSegment(segment)}
                  {idx < regionPath.length - 1 && (
                    <Flex flexDirection="column" alignItems="center" py="2px">
                      <Icon icon={FiArrowDown} color="muted" iconSize={18} />
                    </Flex>
                  )}
                </React.Fragment>
              ))}
            </Flex>
          </Box>
          {regionIdx < regionMap.length - 1 && (
            <Flex flexDirection="column" alignItems="center" py="2px">
              <Icon icon={FiArrowDown} color="muted" iconSize={18} />
            </Flex>
          )}
        </React.Fragment>
      ))}
    </Flex>
  );
}

class PathFinderToolResult extends Component {
  render() {
    const { tool, onCloseToolResult, isInlineMode = false } = this.props;
    const { raw_result } = tool;

    let pathContent = null;
    try {
      if (raw_result) {
        const data = typeof raw_result === 'string' ? JSON.parse(raw_result) : raw_result;
        const { paths = [], returnPaths = [], results = {} } = data;

        if (paths.length > 0 || returnPaths.length > 0) {
          pathContent = (
            <Flex gap={4} px={2} pb={2} justifyContent="center" flexWrap="wrap" alignItems="flex-start">
              {paths.length > 0 && (
                <Box>
                  <Text as="div" fontWeight="bold" mb={2} small textAlign="center">
                    Forward Path
                  </Text>
                  {paths.map((path, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Box key={i} mb={2}>
                      {renderPath(path, results.reachable)}
                    </Box>
                  ))}
                </Box>
              )}
              {returnPaths.length > 0 && (
                <Box>
                  <Text as="div" fontWeight="bold" mb={2} small textAlign="center">
                    Return Path
                  </Text>
                  {returnPaths.map((path, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Box key={i} mb={2}>
                      {renderPath(path, results.returnReachable)}
                    </Box>
                  ))}
                </Box>
              )}
            </Flex>
          );
        }
      }
    } catch (e) {
      pathContent = null;
    }

    return (
      <ToolResultCard
        tool={tool}
        onCloseToolResult={onCloseToolResult}
        className="tool-result-default"
        title={tool.title || 'Cloud Pathfinder Result'}
        isInlineMode={isInlineMode}
        minHeight={0}
        height="auto"
      >
        {!pathContent ? (
          <Box p={2}>
            <Text color="muted">No results available</Text>
          </Box>
        ) : (
          <Box style={{ overflowY: 'auto' }}>{pathContent}</Box>
        )}
      </ToolResultCard>
    );
  }
}

export default PathFinderToolResult;
